// src/hooks/useAuth.js

import { useState, useEffect, createContext, useContext } from 'react';
import { AuthStorage } from '../services';
import defaultAvatar from '../assets/icons/avatar.svg';

// AuthContext for authentication state
const AuthContext = createContext();

export const useAuthContext = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuthContext must be used within an AuthProvider');
	}
	return context;
};

export const AuthProvider = ({ children }) => {
	const [user, setUser] = useState(null);
	const [token, setToken] = useState(null);
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [accessibleRoutes, setAccessibleRoutes] = useState([]); // New state for accessible routes

	useEffect(() => {
		const initializeAuth = () => {
			const storedToken = AuthStorage.getToken();
			const storedUserData = AuthStorage.getUserData();
			const storedAccessibleRoutes = AuthStorage.getAccessibleRoutes(); // Retrieve accessible routes separately

			if (storedToken && storedUserData) {
				setToken(storedToken);
				setUser(storedUserData);
				setIsAuthenticated(true);
				if (storedAccessibleRoutes) {
					setAccessibleRoutes(storedAccessibleRoutes);
				}
			} else if (storedToken) {
				AuthStorage.removeToken();
			}

			setIsLoading(false);
		};

		initializeAuth();
	}, []);

	const login = (newToken, userData, routes) => {
		if (!userData) {
			console.error('User data is required for login');
			return;
		}

		setToken(newToken);
		setUser(userData);
		AuthStorage.setUserData(userData);
		setAccessibleRoutes(routes);
		AuthStorage.setAccessibleRoutes(routes);
		setIsAuthenticated(true);
	};

	const updateUserData = (updateduserData) => {
		if (!updateduserData) {
			console.error('Updated customer data is required');
			return;
		}

		// Merge updatedUserData into the existing user state,
		// ensuring accessibleRoutes (and other non-profile fields) are preserved.
		setUser((prevUser) => ({
			...prevUser,
			...updateduserData
		}));
		AuthStorage.setUserData({
			...(AuthStorage.getUserData() || {}),
			...updateduserData
		});
		// Note: accessibleRoutes are NOT updated here, as per your instruction.
		// They are managed separately on login/logout.
	};

	const logout = () => {
		AuthStorage.removeToken();
		AuthStorage.removeUserData(); // Ensure user data is also cleared
		AuthStorage.removeAccessibleRoutes(); // Remove accessible routes on logout
		setToken(null);
		setUser(null);
		setAccessibleRoutes([]);
		setIsAuthenticated(false);
	};

	const getUserDisplayName = () => {
		if (!user) return '';
		return `${user.firstName || ''}`.trim();
	};

	const getUserPicture = () => {
		// If user has an explicit picture, use it
		if (user && user.pictureUrl) {
			return user.pictureUrl;
		}
		// Generate initials avatar when no picture is available
		const firstName = user?.firstName || '';
		const lastName = user?.lastName || '';
		const firstInitial = (firstName.trim()[0] || '').toUpperCase();
		const lastInitial = (lastName.trim()[0] || '').toUpperCase();
		const initials = `${firstInitial}${lastInitial}`.trim();
		if (!initials) {
			return defaultAvatar;
		}
		// Create a simple SVG data URL with the initials centered
		const size = 128;
		const bg = '#E8E8EA';
		const fg = '#180A6399';
		const fontSize = 52;
		const svg = `<?xml version="1.0" encoding="UTF-8"?>
      <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <rect width="100%" height="100%" rx="16" ry="16" fill="${bg}"/>
        <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Inter, system-ui, -apple-system, Plus Jakarta Sans" font-size="${fontSize}" font-weight="500" fill="${fg}">${initials}</text>
      </svg>`;
		const encoded =
			typeof window !== 'undefined' && window.btoa
				? window.btoa(svg)
				: Buffer.from(svg).toString('base64');
		return `data:image/svg+xml;base64,${encoded}`;
	};

	const getUserEmail = () => {
		if (!user) return '';
		return user.email || '';
	};

	const auth = {
		user,
		token,
		isAuthenticated,
		isLoading,
		login,
		logout,
		updateUserData,
		getUserDisplayName,
		getUserPicture,
		getUserEmail,
		accessibleRoutes // Make accessibleRoutes available in the context
	};

	return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};
